// Google Analytics 4 故障排除工具

export interface GA4TroubleshootResult {
  trackingId: string
  issues: string[]
  suggestions: string[]
  dataCollectionStatus: 'working' | 'blocked' | 'unknown'
  commonCauses: string[]
}

export class GA4Troubleshooter {
  private trackingId = 'G-E5X30486VJ'
  
  /**
   * 全面診斷 GA4 資料收集問題
   */
  async diagnose(): Promise<GA4TroubleshootResult> {
    const result: GA4TroubleshootResult = {
      trackingId: this.trackingId,
      issues: [],
      suggestions: [],
      dataCollectionStatus: 'unknown',
      commonCauses: []
    }

    console.group('🔍 GA4 故障排除診斷')
    
    // 1. 檢查腳本載入
    const scriptLoaded = this.checkScriptLoading()
    if (!scriptLoaded.success) {
      result.issues.push('❌ GA4 腳本未正確載入')
      result.suggestions.push('檢查網路連接或廣告攔截器設定')
    }
    
    // 2. 檢查 gtag 函數
    const gtagAvailable = this.checkGtagFunction()
    if (!gtagAvailable) {
      result.issues.push('❌ gtag 函數不可用')
      result.suggestions.push('確認 GA4 腳本載入完成且未被攔截')
    }
    
    // 3. 檢查網域配置
    const domainIssues = this.checkDomainConfiguration()
    if (domainIssues.length > 0) {
      result.issues.push(...domainIssues)
      result.suggestions.push('在 GA4 屬性中添加正確的網域')
    }
    
    // 4. 檢查隱私設定
    const privacyIssues = this.checkPrivacySettings()
    if (privacyIssues.length > 0) {
      result.issues.push(...privacyIssues)
      result.suggestions.push('檢查使用者同意和隱私設定')
    }
    
    // 5. 測試資料發送
    const dataSendingTest = await this.testDataSending()
    if (!dataSendingTest.success) {
      result.issues.push('❌ 測試事件發送失敗')
      result.suggestions.push('檢查網路連接和防火牆設定')
      result.dataCollectionStatus = 'blocked'
    } else {
      result.dataCollectionStatus = 'working'
    }
    
    // 6. 常見原因檢查
    result.commonCauses = this.getCommonCauses()
    
    console.groupEnd()
    
    this.displayResults(result)
    return result
  }
  
  /**
   * 檢查腳本載入狀態
   */
  private checkScriptLoading(): { success: boolean; details: string } {
    const scripts = document.querySelectorAll('script[src*="googletagmanager.com"]')
    const hasScript = scripts.length > 0
    
    console.log(`📜 GA4 腳本檢查: ${hasScript ? '✅ 已載入' : '❌ 未找到'} (${scripts.length} 個腳本)`)
    
    if (hasScript) {
      scripts.forEach((script, index) => {
        console.log(`   腳本 ${index + 1}: ${script.getAttribute('src')}`)
      })
    }
    
    return {
      success: hasScript,
      details: hasScript ? `找到 ${scripts.length} 個 GA 腳本` : '未找到 GA 腳本'
    }
  }
  
  /**
   * 檢查 gtag 函數可用性
   */
  private checkGtagFunction(): boolean {
    const gtagAvailable = typeof (window as any).gtag === 'function'
    console.log(`🏷️ gtag 函數: ${gtagAvailable ? '✅ 可用' : '❌ 不可用'}`)
    
    if (gtagAvailable) {
      console.log('   gtag 佇列長度:', ((window as any).gtag as any).q?.length || 0)
    }
    
    return gtagAvailable
  }
  
  /**
   * 檢查網域配置問題
   */
  private checkDomainConfiguration(): string[] {
    const issues: string[] = []
    const currentDomain = window.location.hostname
    
    console.log(`🌐 當前網域: ${currentDomain}`)
    
    // 檢查是否為本地環境
    if (['localhost', '127.0.0.1'].includes(currentDomain)) {
      issues.push('⚠️ 本地環境，GA4 資料可能不會顯示')
    }
    
    // 檢查是否為開發網域
    if (currentDomain.includes('.pages.dev')) {
      issues.push('⚠️ Cloudflare Pages 預覽網域，確認 GA4 屬性包含此網域')
    }
    
    return issues
  }
  
  /**
   * 檢查隱私和同意設定
   */
  private checkPrivacySettings(): string[] {
    const issues: string[] = []
    
    // 檢查廣告同意
    const adsConsent = localStorage.getItem('ads-consent')
    console.log(`🔒 廣告同意狀態: ${adsConsent || '未設定'}`)
    
    if (adsConsent === 'false') {
      issues.push('⚠️ 使用者拒絕廣告同意，可能影響資料收集')
    }
    
    // 檢查 Cookie 是否啟用
    if (!navigator.cookieEnabled) {
      issues.push('❌ Cookie 已停用，GA4 無法正常運作')
    }
    
    // 檢查 Do Not Track
    if ((navigator as any).doNotTrack === '1') {
      issues.push('⚠️ 使用者啟用了 Do Not Track')
    }
    
    return issues
  }
  
  /**
   * 測試資料發送
   */
  private async testDataSending(): Promise<{ success: boolean; details: string }> {
    return new Promise((resolve) => {
      try {
        if (typeof (window as any).gtag === 'function') {
          console.log('📊 發送測試事件...')
          
          // 發送測試事件
          ;(window as any).gtag('event', 'ga4_troubleshoot_test', {
            event_category: 'Troubleshoot',
            event_label: 'Data Collection Test',
            value: Date.now(),
            debug_mode: true,
            transport_type: 'beacon'
          })
          
          // 簡單檢測（實際需要檢查網路請求）
          setTimeout(() => {
            console.log('✅ 測試事件已發送')
            resolve({ success: true, details: '測試事件已發送' })
          }, 1000)
        } else {
          resolve({ success: false, details: 'gtag 函數不可用' })
        }
      } catch (error) {
        console.error('測試發送失敗:', error)
        resolve({ success: false, details: `發送失敗: ${error}` })
      }
    })
  }
  
  /**
   * 獲取常見原因列表
   */
  private getCommonCauses(): string[] {
    return [
      '🕐 新的 GA4 屬性需要 24-48 小時才會顯示資料',
      '🛡️ 廣告攔截器（如 uBlock Origin、AdBlock Plus）',
      '🔒 瀏覽器隱私設定（如 Safari 智慧追蹤防護）',
      '🌐 網域未在 GA4 屬性中正確設定',
      '📱 移動設備的隱私限制',
      '🏢 企業防火牆阻擋 Google Analytics',
      '👤 使用者選擇退出資料收集',
      '⚙️ GA4 屬性配置錯誤（如資料保留設定）'
    ]
  }
  
  /**
   * 顯示診斷結果
   */
  private displayResults(result: GA4TroubleshootResult): void {
    console.group('📋 GA4 故障排除報告')
    
    console.log(`🆔 追蹤 ID: ${result.trackingId}`)
    console.log(`📊 資料收集狀態: ${this.getStatusEmoji(result.dataCollectionStatus)} ${result.dataCollectionStatus}`)
    
    if (result.issues.length > 0) {
      console.group('⚠️ 發現的問題')
      result.issues.forEach(issue => console.warn(issue))
      console.groupEnd()
    }
    
    if (result.suggestions.length > 0) {
      console.group('💡 建議解決方案')
      result.suggestions.forEach(suggestion => console.info(`• ${suggestion}`))
      console.groupEnd()
    }
    
    console.group('🔍 常見原因（如果資料仍未出現）')
    result.commonCauses.forEach(cause => console.info(cause))
    console.groupEnd()
    
    console.log('📖 詳細資訊請查看: https://support.google.com/analytics/answer/9304153')
    console.groupEnd()
  }
  
  private getStatusEmoji(status: string): string {
    switch (status) {
      case 'working': return '✅'
      case 'blocked': return '❌'
      default: return '❓'
    }
  }
}

/**
 * 控制台快速診斷指令
 */
export function setupGA4Troubleshoot(): void {
  if (typeof window !== 'undefined') {
    const troubleshooter = new GA4Troubleshooter()
    
    // 提供全域調試指令
    ;(window as any).GA4Troubleshoot = {
      async diagnose() {
        return await troubleshooter.diagnose()
      },
      
      quickCheck() {
        console.log('🔧 GA4 快速檢查:')
        console.log(`📊 追蹤 ID: G-E5X30486VJ`)
        console.log(`🌐 當前網域: ${window.location.hostname}`)
        console.log(`🏷️ gtag 可用: ${typeof (window as any).gtag === 'function' ? '✅' : '❌'}`)
        console.log(`🔒 廣告同意: ${localStorage.getItem('ads-consent') || '未設定'}`)
        console.log(`📜 GA 腳本: ${document.querySelectorAll('script[src*="googletagmanager.com"]').length} 個`)
      }
    }
    
    console.log('🔧 GA4 故障排除工具已載入')
    console.log('   使用 GA4Troubleshoot.diagnose() 進行完整診斷')
    console.log('   使用 GA4Troubleshoot.quickCheck() 進行快速檢查')
  }
} 